import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface HeaderPnlContextType {
  /** Whether the account's overall P&L is shown in the header. */
  isHeaderPnlVisible: boolean;
  toggleHeaderPnl: () => void;
  setHeaderPnlVisible: (visible: boolean) => void;
}

const HeaderPnlContext = createContext<HeaderPnlContextType | undefined>(undefined);

const STORAGE_KEY = 'showHeaderPnl';

/**
 * Shown unless the user has said otherwise. Stored as a string rather than parsed as JSON so
 * a corrupt value cannot throw; anything that is not an explicit "false" means visible.
 */
const readStoredVisibility = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

interface HeaderPnlProviderProps {
  children: ReactNode;
}

/**
 * Whether the header shows the account's P&L.
 *
 * The switch is in the profile menu and the figure is in the middle of the header, two
 * components with no relationship to each other, so the preference lives here. It is kept on
 * the device rather than in the user's document because it is about what is comfortable to
 * have on screen — someone screen-sharing wants it off on that machine only.
 */
export const HeaderPnlProvider: React.FC<HeaderPnlProviderProps> = ({ children }) => {
  const [isHeaderPnlVisible, setHeaderPnlVisible] = useState<boolean>(readStoredVisibility);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isHeaderPnlVisible));
    } catch {
      // A preference that cannot be remembered still works for this session.
    }
  }, [isHeaderPnlVisible]);

  const toggleHeaderPnl = () => setHeaderPnlVisible(previous => !previous);

  return (
    <HeaderPnlContext.Provider value={{ isHeaderPnlVisible, toggleHeaderPnl, setHeaderPnlVisible }}>
      {children}
    </HeaderPnlContext.Provider>
  );
};

export const useHeaderPnl = (): HeaderPnlContextType => {
  const context = useContext(HeaderPnlContext);
  if (context === undefined) {
    throw new Error('useHeaderPnl must be used within a HeaderPnlProvider');
  }
  return context;
};
